
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

const AIStagingPage = () => {
  const [activeDemo, setActiveDemo] = useState('before-after');
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const stagingExamples = [
    {
      id: 'living-room',
      title: 'Modern Living Room',
      before: 'https://public.readdy.ai/ai/img_res/839e2c79ec35affedd2efe03f70b36f3.jpg',
      after: 'https://readdy.ai/api/search-image?query=beautifully%20staged%20modern%20living%20room%20with%20contemporary%20furniture%20gray%20sofa%20coffee%20table%20artwork%20plants%20warm%20lighting%20cozy%20atmosphere%20real%20estate%20photography&width=600&height=400&seq=staging-after-1&orientation=landscape',
      style: 'Modern Contemporary'
    },
    {
      id: 'bedroom',
      title: 'Master Bedroom',
      before: 'https://readdy.ai/api/search-image?query=empty%20bedroom%20with%20white%20walls%20carpet%20large%20window%20minimal%20unfurnished%20space%20real%20estate%20photography%20clean&width=600&height=400&seq=staging-before-2&orientation=landscape',
      after: 'https://readdy.ai/api/search-image?query=elegantly%20staged%20master%20bedroom%20with%20king%20bed%20white%20bedding%20nightstands%20lamps%20artwork%20warm%20lighting%20luxury%20hotel%20style%20real%20estate%20photography&width=600&height=400&seq=staging-after-2&orientation=landscape',
      style: 'Luxury Hotel'
    },
    {
      id: 'kitchen',
      title: 'Gourmet Kitchen',
      before: 'https://readdy.ai/api/search-image?query=empty%20kitchen%20with%20white%20cabinets%20granite%20countertops%20stainless%20appliances%20no%20decor%20minimal%20unfurnished%20real%20estate%20photography&width=600&height=400&seq=staging-before-3&orientation=landscape',
      after: 'https://readdy.ai/api/search-image?query=beautifully%20staged%20kitchen%20with%20decorative%20bowls%20fresh%20flowers%20cutting%20board%20wine%20glasses%20pendant%20lighting%20warm%20inviting%20atmosphere%20real%20estate%20photography&width=600&height=400&seq=staging-after-3&orientation=landscape',
      style: 'Warm Traditional'
    }
  ];

  const features = [
    {
      icon: 'ri-magic-line',
      title: 'AI-Powered Staging',
      description: 'Advanced AI algorithms analyze room layouts and automatically add appropriate furniture and decor'
    },
    {
      icon: 'ri-palette-line',
      title: 'Multiple Style Options',
      description: 'Choose from 15+ interior design styles including modern, traditional, minimalist, and luxury'
    },
    {
      icon: 'ri-time-line',
      title: 'Instant Results',
      description: 'Get professionally staged photos in minutes, not days or weeks'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Effective',
      description: 'Save thousands compared to traditional staging while achieving similar visual impact'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile Friendly',
      description: 'Upload and stage photos directly from your smartphone or tablet'
    },
    {
      icon: 'ri-download-line',
      title: 'High Resolution',
      description: 'Download staged images in high resolution perfect for MLS listings and marketing'
    }
  ];

  const pricingPlans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '$29',
      period: '/month',
      features: [
        '50 staged photos per month',
        '5 design styles',
        'Standard resolution',
        'Email support',
        'Basic editing tools'
      ],
      popular: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$79',
      period: '/month',
      features: [
        '200 staged photos per month',
        '15+ design styles',
        'High resolution downloads',
        'Priority support',
        'Advanced editing tools',
        'Batch processing',
        'Custom branding'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$199',
      period: '/month',
      features: [
        'Unlimited staged photos',
        'All design styles',
        'Ultra-high resolution',
        'Dedicated support',
        'API access',
        'Team collaboration',
        'White-label solution',
        'Custom style training'
      ],
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Jennifer Martinez',
      role: 'Luxury Real Estate Agent',
      company: 'Coldwell Banker',
      image: 'https://readdy.ai/api/search-image?query=professional%20hispanic%20woman%20real%20estate%20agent%20business%20portrait%20smiling%20confident%20modern%20office%20background&width=80&height=80&seq=testimonial-staging-1&orientation=squarish',
      quote: 'AI staging has revolutionized my listings. Properties sell 40% faster and for 8% more than unstaged homes.',
      rating: 5
    },
    {
      name: 'David Chen',
      role: 'Real Estate Photographer',
      company: 'Chen Photography',
      image: 'https://readdy.ai/api/search-image?query=professional%20asian%20man%20photographer%20business%20portrait%20smiling%20confident%20studio%20background%20camera%20equipment&width=80&height=80&seq=testimonial-staging-2&orientation=squarish',
      quote: 'This tool has expanded my service offerings. Clients love the before and after comparisons.',
      rating: 5
    },
    {
      name: 'Sarah Thompson',
      role: 'Broker Owner',
      company: 'Thompson Realty',
      image: 'https://readdy.ai/api/search-image?query=professional%20caucasian%20woman%20real%20estate%20broker%20business%20portrait%20smiling%20confident%20office%20background&width=80&height=80&seq=testimonial-staging-3&orientation=squarish',
      quote: 'Our entire team uses AI staging. It\'s cost-effective and delivers consistent, professional results.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                AI Virtual Staging
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Transform empty properties into beautifully furnished homes with AI-powered virtual staging. Increase buyer interest and sell faster with professional staging at a fraction of the cost.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 text-lg font-semibold whitespace-nowrap">
                  Start Free Trial
                </Button>
                <Button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 text-lg font-semibold whitespace-nowrap">
                  View Examples
                </Button>
              </div>
              <div className="flex items-center space-x-6 text-blue-100">
                <div className="flex items-center space-x-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span>No physical staging required</span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="ri-check-line text-green-400"></i>
                  <span>Results in minutes</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=split%20screen%20comparison%20empty%20room%20versus%20beautifully%20staged%20room%20with%20modern%20furniture%20AI%20virtual%20staging%20technology%20real%20estate%20photography%20professional&width=600&height=400&seq=staging-hero&orientation=landscape"
                alt="AI Virtual Staging Comparison"
                className="rounded-lg shadow-2xl object-cover w-full h-96"
              />
              <div className="absolute top-4 left-4 bg-white text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                Before & After
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              See AI Staging in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the power of AI virtual staging with our interactive before and after gallery
            </p>
          </div>

          <div className="mb-8">
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setActiveDemo('before-after')}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  activeDemo === 'before-after'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                Before & After
              </button>
              <button
                onClick={() => setActiveDemo('style-options')}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  activeDemo === 'style-options'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                Style Options
              </button>
            </div>
          </div>

          {activeDemo === 'before-after' && (
            <div className="grid md:grid-cols-3 gap-8">
              {stagingExamples.map((example) => (
                <div key={example.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <div className="relative">
                    <div className="grid grid-cols-2">
                      <div className="relative">
                        <img 
                          src={example.before}
                          alt={`${example.title} - Before`}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">
                          Before
                        </div>
                      </div>
                      <div className="relative">
                        <img 
                          src={example.after}
                          alt={`${example.title} - After`}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-semibold">
                          After
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{example.title}</h3>
                    <p className="text-gray-600 mb-4">Style: {example.style}</p>
                    <Button className="w-full bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                      Try This Style
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeDemo === 'style-options' && (
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { name: 'Modern Contemporary', image: 'modern minimalist living room with clean lines neutral colors sleek furniture contemporary design real estate photography' },
                { name: 'Traditional Classic', image: 'traditional living room with classic furniture warm colors elegant decor timeless design real estate photography' },
                { name: 'Scandinavian', image: 'scandinavian living room with light wood white walls cozy textiles hygge atmosphere real estate photography' },
                { name: 'Industrial Loft', image: 'industrial loft living room with exposed brick metal accents leather furniture urban design real estate photography' },
                { name: 'Luxury Glam', image: 'luxury glamorous living room with velvet furniture gold accents crystal chandelier elegant design real estate photography' },
                { name: 'Farmhouse Rustic', image: 'farmhouse living room with rustic wood furniture neutral colors cozy atmosphere country design real estate photography' },
                { name: 'Mid-Century Modern', image: 'mid century modern living room with vintage furniture bold colors retro design real estate photography' },
                { name: 'Coastal Beach', image: 'coastal living room with light colors natural textures beach inspired decor relaxed atmosphere real estate photography' }
              ].map((style, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
                  <img 
                    src={`https://readdy.ai/api/search-image?query=$%7Bstyle.image%7D&width=300&height=200&seq=style-${index}&orientation=landscape`}
                    alt={style.name}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 text-center">{style.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose AI Virtual Staging?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our AI staging technology delivers professional results faster and more affordably than traditional staging
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Proven ROI for Real Estate Professionals
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See the measurable impact of AI virtual staging on your listings
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">40%</div>
              <div className="text-gray-600">Faster Sales</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">8%</div>
              <div className="text-gray-600">Higher Sale Price</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">90%</div>
              <div className="text-gray-600">Cost Savings vs Traditional</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
              <div className="text-gray-600">Client Satisfaction</div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Cost Comparison</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Traditional Staging</h4>
                <div className="space-y-3 text-gray-600">
                  <div>Initial consultation: $200</div>
                  <div>Furniture rental (2 months): $3,500</div>
                  <div>Setup and removal: $800</div>
                  <div>Storage fees: $400</div>
                  <div className="border-t pt-3 font-bold text-red-600">Total: $4,900</div>
                </div>
              </div>
              <div className="text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">AI Virtual Staging</h4>
                <div className="space-y-3 text-gray-600">
                  <div>Professional plan (2 months): $158</div>
                  <div>Photo editing: $0</div>
                  <div>Setup time: $0</div>
                  <div>Storage: $0</div>
                  <div className="border-t pt-3 font-bold text-green-600">Total: $158</div>
                </div>
              </div>
            </div>
            <div className="text-center mt-8">
              <div className="text-3xl font-bold text-green-600">Save $4,742 per listing!</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your staging needs and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <div 
                key={plan.id}
                className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                  plan.popular ? 'ring-2 ring-blue-600 transform scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="bg-blue-600 text-white text-center py-2 font-semibold">
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <i className="ri-check-line text-green-500"></i>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full py-3 font-semibold whitespace-nowrap ${
                      plan.popular 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : 'border border-blue-600 text-blue-600 hover:bg-blue-50'
                    }`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {plan.popular ? 'Start Free Trial' : 'Choose Plan'}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">All plans include 14-day free trial • No setup fees • Cancel anytime</p>
            <Button className="border border-gray-300 text-gray-600 hover:bg-gray-50 px-8 py-3 whitespace-nowrap">
              Compare All Features
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Real Estate Professionals Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of agents who are already using AI staging to sell properties faster
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                    <p className="text-gray-500 text-sm">{testimonial.company}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-gray-600 italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Listings?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Start your free trial today and see how AI virtual staging can help you sell properties faster and for more money.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold whitespace-nowrap">
              Start Free 14-Day Trial
            </Button>
            <Button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg font-semibold whitespace-nowrap">
              Schedule Demo
            </Button>
          </div>
          <p className="text-blue-100 mt-6">No credit card required • Full access to all features</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIStagingPage;
